/**
 * WF-Layout - Figma-style auto-layout container for wizard forms
 *
 * A flexible layout component supporting both flexbox and CSS grid modes
 * with independent horizontal/vertical gutters and padding.
 *
 * @example
 * <!-- Flex mode (default) -->
 * <wf-layout direction="column" gap="md">
 *   <wf-input name="name" label="Name"></wf-input>
 *   <wf-email name="email" label="Email"></wf-email>
 * </wf-layout>
 *
 * <!-- Grid mode with responsive columns -->
 * <wf-layout mode="grid" columns="2" gap-x="lg" gap-y="md">
 *   <wf-option value="a">A</wf-option>
 *   <wf-option value="b">B</wf-option>
 * </wf-layout>
 */

import { LitElement, html, type CSSResultGroup } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';

import { wfLayoutStyles } from './wf-layout.styles.js';

export type LayoutMode = 'flex' | 'grid';
export type FlexDirection = 'row' | 'column';
export type AlignItems = 'start' | 'center' | 'end' | 'stretch';
export type JustifyContent = 'start' | 'center' | 'end' | 'space-between' | 'space-around';
export type WidthBehavior = 'full' | 'auto' | 'fit';
export type SpacingPreset = 'none' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';

// Spacing scale in pixels
const SPACING_SCALE: Record<SpacingPreset, number> = {
  none: 0,
  xs: 4,
  sm: 8,
  md: 16,
  lg: 24,
  xl: 32,
};

@customElement('wf-layout')
export class WfLayout extends LitElement {
  static styles: CSSResultGroup = wfLayoutStyles;

  /**
   * Layout mode: 'flex' for flexbox, 'grid' for CSS grid
   */
  @property({ type: String })
  mode: LayoutMode = 'flex';

  /**
   * Flex direction (flex mode only)
   */
  @property({ type: String })
  direction: FlexDirection = 'column';

  /**
   * Uniform gap (sets both gap-x and gap-y)
   */
  @property({ type: String })
  gap: SpacingPreset | string = 'none';

  /**
   * Horizontal gutter (column-gap)
   */
  @property({ type: String, attribute: 'gap-x' })
  gapX: SpacingPreset | string = '';

  /**
   * Vertical gutter (row-gap)
   */
  @property({ type: String, attribute: 'gap-y' })
  gapY: SpacingPreset | string = '';

  /**
   * Cross-axis alignment
   */
  @property({ type: String })
  align: AlignItems = 'stretch';

  /**
   * Main-axis justification
   */
  @property({ type: String })
  justify: JustifyContent = 'start';

  /**
   * Uniform padding (all sides)
   */
  @property({ type: String })
  padding: SpacingPreset | string = 'none';

  /**
   * Horizontal padding (left/right)
   */
  @property({ type: String, attribute: 'padding-x' })
  paddingX: SpacingPreset | string = '';

  /**
   * Vertical padding (top/bottom)
   */
  @property({ type: String, attribute: 'padding-y' })
  paddingY: SpacingPreset | string = '';

  /**
   * Enable flex-wrap (flex mode only)
   */
  @property({ type: Boolean })
  wrap = false;

  /**
   * Width behavior
   */
  @property({ type: String })
  width: WidthBehavior = 'full';

  /**
   * Number of columns or 'auto' for responsive (grid mode only)
   */
  @property({ type: String })
  columns: string = 'auto';

  /**
   * Minimum item width for auto-fit grid
   */
  @property({ type: String, attribute: 'min-item-width' })
  minItemWidth = '200px';

  /**
   * Reference to the slot element
   */
  @query('slot')
  private _slot!: HTMLSlotElement;

  /**
   * Convert spacing value to pixels
   */
  private _resolveSpacing(value: SpacingPreset | string): string {
    if (!value || value === 'none') {
      return '0';
    }

    // Check if it's a preset
    if (value in SPACING_SCALE) {
      return `${SPACING_SCALE[value as SpacingPreset]}px`;
    }

    // Check if it's a number (treat as pixels)
    const num = parseFloat(value);
    if (!isNaN(num)) {
      return `${num}px`;
    }

    // Return as-is (could be a CSS value like '1rem')
    return value;
  }

  /**
   * Get computed gap-x value
   */
  private _getGapX(): string {
    return this._resolveSpacing(this.gapX || this.gap);
  }

  /**
   * Get computed gap-y value
   */
  private _getGapY(): string {
    return this._resolveSpacing(this.gapY || this.gap);
  }

  /**
   * Get computed padding values
   */
  private _getPadding(): string {
    const uniformPadding = this._resolveSpacing(this.padding);
    const px = this.paddingX ? this._resolveSpacing(this.paddingX) : uniformPadding;
    const py = this.paddingY ? this._resolveSpacing(this.paddingY) : uniformPadding;
    return `${py} ${px}`;
  }

  /**
   * Build inline styles based on properties
   */
  private _buildStyles(): string {
    const styles: string[] = [];

    if (this.mode === 'grid') {
      styles.push('display: grid');

      // Grid columns
      const cols = parseInt(this.columns, 10);
      if (!isNaN(cols) && cols > 0) {
        styles.push(`grid-template-columns: repeat(${cols}, 1fr)`);
      } else {
        // Auto-fit responsive grid
        styles.push(
          `grid-template-columns: repeat(auto-fit, minmax(${this.minItemWidth}, 1fr))`
        );
      }

      // Grid alignment
      if (this.align !== 'stretch') {
        styles.push(`align-items: ${this.align}`);
      }
      if (this.justify !== 'start') {
        styles.push(`justify-content: ${this.justify}`);
      }
    } else {
      // Flex mode
      styles.push('display: flex');
      styles.push(`flex-direction: ${this.direction}`);

      if (this.wrap) {
        styles.push('flex-wrap: wrap');
      }

      // Flex alignment
      if (this.align !== 'stretch') {
        styles.push(`align-items: ${this.align}`);
      }
      if (this.justify !== 'start') {
        styles.push(`justify-content: ${this.justify}`);
      }
    }

    // Gap (works for both flex and grid)
    const gapX = this._getGapX();
    const gapY = this._getGapY();
    if (gapX !== '0' || gapY !== '0') {
      styles.push(`column-gap: ${gapX}`);
      styles.push(`row-gap: ${gapY}`);
    }

    // Padding
    const padding = this._getPadding();
    if (padding !== '0 0') {
      styles.push(`padding: ${padding}`);
    }

    return styles.join('; ');
  }

  /**
   * Apply data-* attributes from children as inline styles
   * Supports: data-span, data-row-span, data-grow, data-shrink, data-align, data-order
   */
  private _applyChildStyles(): void {
    if (!this._slot) {return;}

    const children = this._slot.assignedElements({ flatten: true });

    for (const child of children) {
      const el = child as HTMLElement;
      const styles: string[] = [];

      // Grid column span
      const span = el.getAttribute('data-span');
      if (span) {
        styles.push(`grid-column: span ${span}`);
      }

      // Grid row span
      const rowSpan = el.getAttribute('data-row-span');
      if (rowSpan) {
        styles.push(`grid-row: span ${rowSpan}`);
      }

      // Flex grow
      if (el.hasAttribute('data-grow')) {
        styles.push('flex-grow: 1');
      }

      // Flex shrink (opt out)
      if (el.hasAttribute('data-shrink')) {
        styles.push('flex-shrink: 0');
      }

      // Alignment override
      const align = el.getAttribute('data-align');
      if (align) {
        styles.push(`align-self: ${align}`);
      }

      // Order
      const order = el.getAttribute('data-order');
      if (order) {
        styles.push(`order: ${order}`);
      }

      // Apply styles if any data attributes were found
      if (styles.length > 0) {
        // Preserve existing inline styles
        const existingStyles = el.style.cssText;
        const newStyles = styles.join('; ');
        el.style.cssText = existingStyles ? `${existingStyles}; ${newStyles}` : newStyles;
      }
    }
  }

  /**
   * Handle slot changes to apply child styles
   */
  private _handleLayoutSlotChange(): void {
    this._applyChildStyles();
  }

  /**
   * Apply styles to host element when properties change
   */
  updated(changedProperties: Map<string, unknown>) {
    super.updated(changedProperties);

    // Apply layout styles to host element
    this.style.cssText = this._buildStyles();

    // Update width class on host
    this.classList.remove('wf-layout--width-full', 'wf-layout--width-auto', 'wf-layout--width-fit');
    this.classList.add(`wf-layout--width-${this.width}`);

    // Apply data-* attribute styles to children
    this._applyChildStyles();
  }

  render() {
    // Slotted content flows directly into the host with applied layout styles
    return html`<slot @slotchange="${this._handleLayoutSlotChange}"></slot>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'wf-layout': WfLayout;
  }
}
